import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useState } from 'react';
import { useColorScheme as useSystemColorScheme } from 'react-native';

const THEME_PREFERENCE_KEY = '@flavrly/theme-preference';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ColorScheme = 'light' | 'dark';

/**
 * Hook to manage theme preference with AsyncStorage persistence.
 * Returns the resolved color scheme and methods to update preference.
 */
export function useThemePreference() {
  const systemColorScheme = useSystemColorScheme();
  const [preference, setPreferenceState] = useState<ThemePreference>('system');
  const [isLoading, setIsLoading] = useState(true);

  // Load preference from AsyncStorage on mount
  useEffect(() => {
    const loadPreference = async () => {
      try {
        const stored = await AsyncStorage.getItem(THEME_PREFERENCE_KEY);
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
          setPreferenceState(stored);
        }
      } catch (error) {
        console.error('Failed to load theme preference:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreference();
  }, []);

  // Save preference to AsyncStorage
  const setPreference = useCallback(async (newPreference: ThemePreference) => {
    try {
      await AsyncStorage.setItem(THEME_PREFERENCE_KEY, newPreference);
      setPreferenceState(newPreference);
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  }, []);

  // Toggle between light and dark (or set explicit preference if currently system)
  const toggleDarkMode = useCallback(async () => {
    const currentResolved = preference === 'system'
      ? (systemColorScheme ?? 'light')
      : preference;

    const newPreference: ThemePreference = currentResolved === 'dark' ? 'light' : 'dark';
    await setPreference(newPreference);
  }, [preference, systemColorScheme, setPreference]);

  // Check if dark mode is enabled
  const isDarkMode = preference === 'system'
    ? systemColorScheme === 'dark'
    : preference === 'dark';

  // Resolve the actual color scheme
  const colorScheme: ColorScheme = preference === 'system'
    ? (systemColorScheme === 'dark' ? 'dark' : 'light')
    : preference;

  return {
    /** The user's preference ('light', 'dark', or 'system') */
    preference,
    /** The resolved color scheme to use */
    colorScheme,
    /** Whether dark mode is currently active */
    isDarkMode,
    /** Whether we're still loading the preference from storage */
    isLoading,
    /** Set the theme preference */
    setPreference,
    /** Toggle dark mode on/off */
    toggleDarkMode,
  };
}
